import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import { ApplicationService } from '../../../services/application/application.service';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { ApplicationState } from '../../../classes/ApplicationState';
import { ModalService } from '../../../services/modal/modal.service';
import { ModalType, CoverpathURLs } from '../../../../js/interfaces/CoverpathTypes';
import { AmorphicService } from '../../../services/amorphic/amorphic.service';
import {
  TotalAnsweredQuestionsForProgressPercentageInterface,
  TotalQuestionsForProgressPercentageInterface,
} from '../../../interfaces/Type';
import { SegmentEventAction } from '../../../services/analytics/SegmentInterfaces';
import { SegmentService } from '../../../services/analytics/segment.service';
import { productTypes } from '../../../../../common/js/static/Product';
import { CoverpathControllerInterface } from '../../../interfaces/controller/CoverpathControllerInterface';
import type { CasApplication } from '../../../../classes/coverpath_subclasses/CasApplication';
import { CoverpathWholeLifeQuote } from '../../../../classes/quote/coverpath/CoverpathWholeLifeQuote';
import { CoverpathTermQuote } from '../../../../classes/quote/coverpath/CoverpathTermQuote';
import { ProductType } from '../../../../../common/js/interfaces/CommonTypes';
import { ApplicationSectionUtils, ApplicationSectionsList } from '../../../classes/ApplicationSection';
import { Subscription } from 'rxjs';
import { ToastService } from '../../overlays/toaster/toast-service.service';
import { CasMetadataNode } from '../../../../../coverpath-common/angular/classes/CasMetadataNode';
import { MMDirectApplication } from '../../../../../coverpath-common/classes/mmdirect_subclasses/MMDirectApplication';
import { UICategory, UILoggerSingleton } from '../../../../../common/js/logger/UILoggerSingleton';

@Component({
  moduleId: module.id,
  selector: 'section-menu',
  templateUrl: './section-menu.component.html',
})
export class SectionMenuComponent implements OnInit {
  appState: ApplicationState;
  activeSection: number;
  controller: CoverpathControllerInterface;
  private numberOfTotalQuestionsInSection = {};
  private numberOfAnsweredQuestionsInSection = {};
  appIsLocked: boolean;
  public sendDisabled: boolean = false;
  public submitDisabled: boolean = false;
  @Output() goToError: EventEmitter<void> = new EventEmitter();
  @Output() showNavToast: EventEmitter<void> = new EventEmitter();
  @Output() goToCaseSetup: EventEmitter<void> = new EventEmitter();
  isNewYorkApplication: boolean = false;
  private afterSectionLoadedSubscription: Subscription;
  private questionAnsweredSubscription: Subscription;
  private sectionValidationSubscription: Subscription;
  private questionsAnsweredSub: Subscription;
  private sendButtonDisabledSusbcription: Subscription;
  private advisorSubmittedApplicationSentSusbcription: Subscription;
  public isAgentSubmittedAppEnabled: boolean = false;

  constructor(
    private applicationService: ApplicationService,
    private navigationService: NavigationService,
    private router: Router,
    private modalService: ModalService,
    private amorphicService: AmorphicService,
    private toastService: ToastService,
    private segmentService: SegmentService
  ) {
    this.appState = applicationService.appState;
    this.controller = this.amorphicService.controller;
    this.appIsLocked = !!this.amorphicService.controller.isLocked;
    this.isNewYorkApplication = this.controller.customer.application.primaryPolicy.isNewYorkApplication();
  }

  ngOnInit() {
    this.isAgentSubmittedAppEnabled = this.controller.isAgentSubmittedAppEnabled;
    this.sendDisabled =
      this.controller.customer.application.isTeleCMICompleted || this.controller.nbDecommissionPost30Days;
    const caseSetupNode: CasMetadataNode = this.appState.appMetadata.children.filter(
      (node) => node.section === ApplicationSectionsList.CASE_SETUP
    )[0];

    const verifyNode: CasMetadataNode = this.appState.appMetadata.children.filter(
      (node) => node.section === ApplicationSectionsList.VERIFY
    )[0];

    ApplicationSectionUtils.filterEmptySections(this.appState);

    if (ApplicationSectionUtils.shouldHideCaseSetup(this.appState)) {
      ApplicationSectionUtils.filterSection(ApplicationSectionsList.CASE_SETUP, this.appState);
    }

    if (
      ApplicationSectionUtils.shouldDisableSection(
        this.amorphicService.controller.customer.application.primaryPolicy,
        ApplicationSectionsList.SOURCE_OF_FUNDS
      )
    ) {
      if (
        this.appState.appMetadata.children.some(
          (section) => section.section === ApplicationSectionsList.SOURCE_OF_FUNDS
        )
      ) {
        ApplicationSectionUtils.filterSection(ApplicationSectionsList.SOURCE_OF_FUNDS, this.appState);
      }
    }

    this.navigationService.activeApplicationSection$.subscribe((activeSection) => {
      this.activeSection = activeSection;
    });
    this.applicationService.questionAnsweredObservable$.subscribe(() => {
      let totalQuestions = 0;
      let totalAnsweredQuestions = 0;

      for (const property in this.numberOfTotalQuestionsInSection) {
        totalQuestions += this.numberOfTotalQuestionsInSection[property];
      }

      for (const property in this.numberOfAnsweredQuestionsInSection) {
        totalAnsweredQuestions += this.numberOfAnsweredQuestionsInSection[property];
      }

      const percentFilled = Math.round((totalAnsweredQuestions / totalQuestions) * 100) / 100;
      if (!this.appIsLocked) {
        (this.amorphicService.controller.customer.application as CasApplication).progressPercentage =
          percentFilled > 1 ? 1 : percentFilled;
      }
    });

    this.sendButtonDisabledSusbcription = this.applicationService.sendButtonDisabledObservable$.subscribe(
      (sendButtonDisabled) => {
        this.sendDisabled = sendButtonDisabled;
      }
    );

    this.advisorSubmittedApplicationSentSusbcription =
      this.applicationService.advisorSubmittedApplicationSentObservable$.subscribe(
        (advisorSubmittedApplicationSent) => {
          this.submitDisabled = advisorSubmittedApplicationSent;
        }
      );

    this.questionAnsweredSubscription = this.applicationService.questionAnsweredObservable$.subscribe(() => {
      // Hide Tele-CMI Case Setup section on client side or if tele-CMI is not required
      if (ApplicationSectionUtils.shouldHideCaseSetup(this.appState)) {
        // If case setup node exists, filter it out
        if (
          this.appState.appMetadata.children.some((section) => section.section === ApplicationSectionsList.CASE_SETUP)
        ) {
          ApplicationSectionUtils.filterSection(ApplicationSectionsList.CASE_SETUP, this.appState);
          this.navigationService.activeApplicationSection$.next(this.navigationService.currentSection - 1);
        }
      }
      // Add Case Case setup node back if it doesn't already exist and isTeleCMI is true and advisor is logged in
      if (!ApplicationSectionUtils.shouldHideCaseSetup(this.appState)) {
        if (
          !this.appState.appMetadata.children.some((section) => section.section === ApplicationSectionsList.CASE_SETUP)
        ) {
          this.appState.appMetadata.children.unshift(caseSetupNode);
          this.navigationService.activeApplicationSection$.next(this.navigationService.currentSection + 1);
        }
      }

      //Remove Verify section if AgentSubmittedApp
      if (ApplicationSectionUtils.shouldHideVerify(this.appState)) {
        if (this.appState.appMetadata.children.some((section) => section.section === ApplicationSectionsList.VERIFY)) {
          ApplicationSectionUtils.filterSection(ApplicationSectionsList.VERIFY, this.appState);
        }
      }

      //Add Verify section back if not AgentSubmittedApp
      if (!ApplicationSectionUtils.shouldHideVerify(this.appState)) {
        if (!this.appState.appMetadata.children.some((section) => section.section === ApplicationSectionsList.VERIFY)) {
          this.appState.appMetadata.children.push(verifyNode);
        }
      }
    });
  }

  ngOnDestroy() {
    if (this.afterSectionLoadedSubscription) {
      this.afterSectionLoadedSubscription.unsubscribe();
    }
    if (this.questionAnsweredSubscription) {
      this.questionAnsweredSubscription.unsubscribe();
    }
    if (this.sectionValidationSubscription) {
      this.sectionValidationSubscription.unsubscribe();
    }
    if (this.questionsAnsweredSub) {
      this.questionsAnsweredSub.unsubscribe();
    }
    if (this.sendButtonDisabledSusbcription) {
      this.sendButtonDisabledSusbcription.unsubscribe();
    }
    if (this.advisorSubmittedApplicationSentSusbcription) {
      this.advisorSubmittedApplicationSentSusbcription.unsubscribe();
    }
  }

  navigate(section) {
    if (this.appState.isAdvisor || this.appState.previousSectionsComplete(section)) {
      this.navigationService.activeApplicationSection$.next(section);
    } else {
      this.showNavToast.emit();
    }
  }

  exit() {
    if (this.appState.isAdvisor) {
      //todo: HL-7442 will add a modal for agent to double check exit or not for day 2
      //need to clean up customer and application once we leave the application page.
      this.router.navigate(['/account-center']);
    } else {
      this.router.navigate(['/account-center']);
    }
  }

  async send() {
    if (this.appState.isAdvisor) {
      let isAgentSubmittedApp = false;
      const application = this.amorphicService.controller.customer.application;
      const isCoverpathPolicy = this.amorphicService.controller.customer.application.primaryPolicy.isCoverpathPolicy();
      const isAgentSubmittedAppEnabled = this.amorphicService.controller.isAgentSubmittedAppEnabled;
      const isAppCompleteForClientSubmission = this.appState.isAppCompleteForClientSubmission();
      const isAppCompleteForAgentSubmission = this.appState.isAppCompleteForAgentSubmission();
      const hasAgentAnsweredAllLockedQuestions = this.appState.hasAgentAnsweredAllLockedQuestions();
      const isTeleInterviewerLoggedIn = this.appState.isTeleInterviewerLoggedIn;

      if (isCoverpathPolicy && isAgentSubmittedAppEnabled) {
        isAgentSubmittedApp = (application as CasApplication).isAgentSubmittedApp;
      }
      /* Show modal if:
         1) A question is missing in any section (!isAppCompleteForAgentSubmission) and it's agent submitted or a Tele Interviewer logged in
         2) A question is missing in the Case Details section (!isAppCompleteForClientSubmission) and it's client submitted
       */
      if (
        (!isAppCompleteForAgentSubmission && (isAgentSubmittedApp || isTeleInterviewerLoggedIn)) ||
        (!isAppCompleteForClientSubmission && !isAgentSubmittedApp) ||
        (isAgentSubmittedApp && isCoverpathPolicy && application.primaryPolicy.isGiactWebDebitValid === false)
      ) {
        const modalType = {
          type: ModalType.advisorAppIncomplete,
          modalData: {
            isTeleInterviewerLoggedIn,
            isClientSubmitted: !isAgentSubmittedApp,
          },
        };
        this.handleModalOperation(modalType);
      } else if (isTeleInterviewerLoggedIn) {
        await this.applicationService.handleTeleInterviewerSubmit();
      } else if (
        !hasAgentAnsweredAllLockedQuestions &&
        !isAgentSubmittedApp &&
        !isTeleInterviewerLoggedIn &&
        application.lockedByAdvisor &&
        this.controller.isQuestionLockEnabled
      ) {
        const modalType = {
          type: ModalType.lockedQuestionIncomplete,
        };
        this.handleModalOperation(modalType);
      } else {
        const productType = this.amorphicService.controller.customer.application.primaryPolicy.productType;
        const secondaryQuote = application.quote.secondaryQuote;

        const quotes = [];

        this.updateQuotesArrayForPremiumQuoteDialog(quotes, productType, secondaryQuote);

        const modalType = {
          type: ModalType.premiumLessCommonQuote,
          modalData: {
            quotes: quotes,
            isAgentSubmittedApp,
            isTeleCMI: application.isTeleCMI,
          },
        };

        this.modalService.activate(modalType);
        const subscription = this.modalService.userResponse.subscribe(async (dialogResponse) => {
          subscription.unsubscribe();
          if (dialogResponse && dialogResponse['proceed']) {
            this.updateshowPremiumToClientInDatabase(dialogResponse['showPremiumToClient']);
            if (isAgentSubmittedApp) {
              try {
                await this.submitFromSection();
              } catch (error) {} // Error is actually caught and logged in customer.submitPolicy
            } else {
              this.toApplicationToClient(dialogResponse['showPremiumToClient']);
            }
          }
        });
      }
    }
  }

  private handleModalOperation(modalType) {
    this.modalService.activate(modalType);
    const subscription = this.modalService.userResponse.subscribe(async (dialogResponse) => {
      subscription.unsubscribe();
      if (dialogResponse) {
        switch (dialogResponse['action']) {
          case 'Find Error':
            this.goToError.emit();
            break;
          case ApplicationSectionsList.CASE_SETUP:
            this.goToCaseSetup.emit();
            break;
          default:
            break;
        }
      }
    });
  }

  private toApplicationToClient(showPremiumToClient: boolean) {
    this.applicationService
      .sendAAApplicationToClient()
      .then((response) => this.runLinkEvent(response.application, showPremiumToClient));
  }

  private updateshowPremiumToClientInDatabase(showPremiumToClient) {
    this.amorphicService.controller.customer.application.quote['showPremiumToClient'] = showPremiumToClient;
    if (this.amorphicService.controller.customer.application.quote.secondaryQuote) {
      this.amorphicService.controller.customer.application.quote.secondaryQuote['showPremiumToClient'] =
        showPremiumToClient;
    }
  }

  private updateQuotesArrayForPremiumQuoteDialog(quotes, productType, secondaryQuote) {
    if (productType === ProductType.WHOLE && secondaryQuote) {
      const wlPolicy = this.amorphicService.controller.customer.application.primaryPolicy;
      const termPolicy = this.amorphicService.controller.customer.application.secondaryPolicy;
      const totalPremium =
        wlPolicy.premiumBreakdown.getTotalWithPendingAmendment() +
        termPolicy.premiumBreakdown.getTotalWithPendingAmendment();

      quotes.push({
        faceAmount: wlPolicy.faceAmount,
        product: productTypes.Whole.productNames['' + wlPolicy.product + ''],
        premium: totalPremium,
        paymentFrequency: wlPolicy.paymentFrequency,
      });

      quotes.push({
        faceAmount: termPolicy.faceAmount,
        product: termPolicy.term === 1 ? 'Annually Renewable Term' : 'Convertible Term ' + termPolicy.term,
        premium: totalPremium,
        paymentFrequency: termPolicy.paymentFrequency,
      });
    } else {
      if (productType === ProductType.WHOLE) {
        const wlPolicy = this.amorphicService.controller.customer.application.primaryPolicy;

        quotes.push({
          faceAmount: wlPolicy.faceAmount,
          product: productTypes.Whole.productNames['' + wlPolicy.product + ''],
          premium: wlPolicy.premiumBreakdown.getTotalWithPendingAmendment(),
          paymentFrequency: wlPolicy.paymentFrequency,
        });
      } else if (productType === ProductType.TERM) {
        const termPolicy = this.amorphicService.controller.customer.application.primaryPolicy;

        quotes.push({
          faceAmount: termPolicy.faceAmount,
          product: termPolicy.term === 1 ? 'Annually Renewable Term' : 'Convertible Term ' + termPolicy.term,
          premium: termPolicy.premiumBreakdown.getTotalWithPendingAmendment(),
          paymentFrequency: termPolicy.paymentFrequency,
        });
      }
    }
  }

  private runLinkEvent(applicationSent: CasApplication, showPremiumToClient: boolean) {
    if (applicationSent.quote.productType === ProductType.WHOLE) {
      const wlQuote = applicationSent.quote as CoverpathWholeLifeQuote;

      this.segmentService.handleLinkSentEvent(
        applicationSent,
        wlQuote.dividendInterestAdjustment ? wlQuote.dividendInterestAdjustment : 0,
        showPremiumToClient
      );
    } else if (applicationSent.quote.productType === ProductType.TERM) {
      const termQuote = applicationSent.quote as CoverpathTermQuote;

      this.segmentService.handleLinkSentEvent(applicationSent, 0, showPremiumToClient);
    } else {
      this.segmentService.handleLinkSentEvent(applicationSent, 0, true);
    }
  }

  async back() {
    const encodedApplicationId: string =
      await this.amorphicService.controller.accountCenterController.getEncodedApplicationId();

    this.segmentService.handleViewApplicationEvent(SegmentEventAction.RETURN_TO_DETAILS, 'view_application_page');

    if (this.controller.caseRedesignEnabled) {
      this.router.navigate([CoverpathURLs.CASE_DETAILS], { queryParams: { id: encodedApplicationId } });
    } else {
      this.router.navigate([CoverpathURLs.DETAILED_VIEW], { queryParams: { id: encodedApplicationId } });
    }
  }

  onNumberOfTotalQuestionsChange(totalQuestionInSection: TotalQuestionsForProgressPercentageInterface) {
    this.numberOfTotalQuestionsInSection[totalQuestionInSection.sectionName] =
      totalQuestionInSection.numberOfTotalQuestions;
  }

  onNumberOfAnsweredQuestionsChange(
    totalAnsweredQuestionsInSection: TotalAnsweredQuestionsForProgressPercentageInterface
  ) {
    this.numberOfAnsweredQuestionsInSection[totalAnsweredQuestionsInSection.sectionName] =
      totalAnsweredQuestionsInSection.numberOfAnsweredQuestions;
  }

  async submitFromSection() {
    const controller = this.applicationService.appState.controller;

    UILoggerSingleton.getInstance().info(
      {
        module: 'SectionMenuComponent',
        function: 'submitFromSection',
        category: UICategory.request,
      },
      'Performance tracking - Advisor/Proxy has clicked Get My Decision'
    );

    const application: CasApplication | MMDirectApplication = controller.customer.application;

    // @TODO refactor when HL-21333 is done
    const hasAccess = await controller.checkApplicationAccess(application).catch(() => false);
    if (hasAccess) {
      this.applicationService.loadingSubject.next(true);
      const err = await this.applicationService.submitFromService();
      if (err) {
        this.applicationService.loadingSubject.next(false);
        this.applicationService.advisorSubmittedApplicationSentSubject.next(false);
        const error = {
          isHumanRelated: false,
          code: err.code,
          message: err.message,
        };
        UILoggerSingleton.getInstance().error(
          {
            module: 'SectionMenuComponent',
            function: 'submitFromSection',
            category: UICategory.request,
            error: error,
          },
          `submitFromService failed`
        );
        this.toastService.showToast(`Sorry! Something went wrong. Please contact customer support`);
      }

      await this.applicationService.recordCoverpathActiveApps();
      const encodedApplicationId: string =
        await this.amorphicService.controller.accountCenterController.getEncodedApplicationId();
      if (this.controller.caseRedesignEnabled) {
        return this.router.navigate([CoverpathURLs.CASE_DETAILS], {
          queryParams: {
            id: encodedApplicationId,
            ASA: 'true',
            isTeleCMI: application.isTeleCMI ? 'true' : 'false',
          },
        });
      } else {
        return this.router.navigate([CoverpathURLs.DETAILED_VIEW], {
          queryParams: {
            id: encodedApplicationId,
            ASA: 'true',
            isTeleCMI: application.isTeleCMI ? 'true' : 'false',
          },
        });
      }
    }

    UILoggerSingleton.getInstance().error(
      {
        module: 'SectionMenuComponent',
        category: UICategory.request,
        function: 'submitFromSection',
        error: {
          isHumanRelated: false,
        },
      },
      `checkApplicationAccess failed`
    );
    this.applicationService.loadingSubject.next(false);
    this.applicationService.advisorSubmittedApplicationSentSubject.next(false);
    this.toastService.showToast(`Sorry! Something went wrong. Please contact customer support`);
  }

  isDisabled(node): boolean {
    const primaryPolicy = this.amorphicService.controller.customer.application.primaryPolicy;
    const flags = {
      mmGiactWebDebitVerificationServiceEnabled:
        this.amorphicService.controller.mmGiactWebDebitVerificationServiceEnabled,
    };

    return ApplicationSectionUtils.shouldDisableSection(primaryPolicy, node.section, flags);
  }

  isError(node): boolean {
    const primaryPolicy = this.amorphicService.controller.customer.application.primaryPolicy;
    return ApplicationSectionUtils.shouldErrorForPayment(primaryPolicy, node.section);
  }
}
